"use client";

import React, { useRef } from "react";
import FloatingArrow from "../uiDecor/floatingArrow";
import { ContainerProp, descriptionObject } from "../../../global";
import Image from "next/image";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { textTransparent } from "@/app/utils/api-helpers";
import BottomCurves from "../curves/bottomCurves";
import { getStrapiMedia } from "@/utils/api-helpers";

gsap.registerPlugin(ScrollTrigger);

type TextFloatImageProp = Omit<ContainerProp, "className"> & {
    direction: string;
    imagePosition: string;
    text: string;
    description: descriptionObject[];
};

export default function TextFloatImageContainer({
    data,
}: {
    data: TextFloatImageProp;
}) {
    const direction = data.imagePosition;

    const containerRef = useRef(null);
    const imgRef = useRef(null);

    useGSAP(
        () => {
            let textTl = gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top 75%",
                    toggleActions: "play none none reverse",
                },
            });

            let imageTl = gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top 75%",
                    toggleActions: "play none none reverse",
                },
            });

            textTl.to(".textChild", { opacity: 1, stagger: 0.3, y: 0 });
            imageTl.to(".imageChild", { opacity: 1, x: 0 });
        },
        { scope: containerRef }
    );

    return (
        <section
            className={`pt-24 pb-5 lg:pt-[6.25vw] lg:pb-[8.333vw] bg-cover bg-no-repeat bg-lightGrey`}
            ref={containerRef}
        >
            <>
                <div className="relative m-auto text-center md:w-1/2">
                    <FloatingArrow
                        wrapText="traditional-360"
                        direction={
                            direction == "left"
                                ? "right"
                                : direction == "right"
                                ? "left"
                                : ""
                        }
                        className={`!-top-28 md:top-0 ${
                            direction == "left"
                                ? "right-[40%]"
                                : direction == "right"
                                ? "md:-left-[12%] lg:-left-[10%]"
                                : ""
                        }`}
                    />
                    <h6 className=" text-darkGrey font-semibold textChild opacity-0 translate-y-6">
                        {data.text}
                    </h6>
                    {data.header && (
                        <h2 className="h1 text-purple textChild opacity-0 translate-y-6">
                            {textTransparent(data.header)}
                        </h2>
                    )}
                    {data.description.map((desc: any, di: number) => {
                        return (
                            <div key={di}>
                                {desc.type === "paragraph" && (
                                    <p className="textChild opacity-0 translate-y-8">
                                        {desc.children.map(
                                            (para: any, pi: number) => {
                                                if (para.text.trim() !== "") {
                                                    if (para.bold) {
                                                        return (
                                                            <b key={pi}>
                                                                {para.text}
                                                            </b>
                                                        );
                                                    } else {
                                                        return (
                                                            <span key={pi}>
                                                                {para.text}
                                                            </span>
                                                        );
                                                    }
                                                }
                                            }
                                        )}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div
                    className={`relative flex z-10 m-auto aspect-square pointer-events-none md:absolute md:top-0 md:bottom-0 md:translate-y-[-12%] w-full md:w-[58.854vw] h-full md:h-[58.854vw] ${
                        direction == "left"
                            ? "left-0 md:-translate-x-1/3"
                            : direction == "right"
                            ? "right-0 md:translate-x-1/3"
                            : ""
                    }`}
                >
                    <Image
                        src={getStrapiMedia(data.image?.url ?? null)}
                        width={1350}
                        height={1120}
                        alt={data.image?.alternativeText || ""}
                        className={`w-full h-full object-contain imageChild opacity-0 ${
                            direction == "left"
                                ? " md:-translate-x-10"
                                : direction == "right"
                                ? "md:translate-x-10"
                                : ""
                        }`}
                        ref={imgRef}
                        loading="lazy"
                    />
                </div>
            </>
            <BottomCurves
                color="rainbow purple to blue"
                direction={direction}
            />
        </section>
    );
}
